import {
  avatar,
  logo,
  supporticon,
  topbarnotification,
  globeicon,
} from '@assets/icons'
import {
  LogoWrapper,
  StyleTopBar,
  TopbarRightWrapper,
} from '@commons/layouts/style'
import Button from '@components/button'
import Svg from '@components/svg'
import { useDropdown } from '@context/DropdownContext'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { LogOut, Lock, UserPlus, Menu } from 'lucide-react'
import theme from '@utils/theme'
import { useModal } from '@context/ModalContext'
import { Admin, Notification } from '@apis/index'
import { Helper } from '@utils/index'
import ChangePassword from '@components/change-password-modal'
import { RootState } from '@store/index'
import { useEffect, useState } from 'react'
import NotificationDropdown from '@components/notification-dropdown'
import useIsMobile from '@hooks/useIsMobile'

const Topbar = () => {
  const isMobile = useIsMobile()
  const { items: notifications, totalItems } = useSelector(
    (state: RootState) => state.notifications,
  )

  const unreadCount = notifications.filter(
    (notification) => !notification.isRead,
  ).length

  const fetchNotifications = async () => {
    try {
      await Notification.list({
        state: 'notifications',
        params: { limit: 20 },
      })
    } catch (error) {}
  }
  const { showDropdown, hideDropdown } = useDropdown()
  const { showModal } = useModal()
  const navigate = useNavigate()
  const { data: user } = useSelector((state: any) => state.user)

  const handleLogout = async () => {
    await Admin.logout({ state: 'user' }).then(() => {
      Helper.removeUser()
      navigate('/admin-login')
    })
  }
  useEffect(() => {
    fetchNotifications()
  }, [])
  const openUserDropdown = () => {
    showDropdown(
      <DropdownMenu>
        <DropdownItem
          onClick={() => {
            showModal(<ChangePassword />)
            hideDropdown()
          }}
        >
          <Lock size={16} />
          Change Password
        </DropdownItem>

        <DropdownItem onClick={handleLogout}>
          <LogOut size={16} />
          Log Out
        </DropdownItem>
      </DropdownMenu>,
    )
  }

  const openNotificationDropdown = () => {
    showDropdown(
      <NotificationDropdown
        notifications={notifications}
        totalItems={totalItems}
      />,
    )
  }

  return (
    <StyleTopBar>
      <MobileLogoWrapper>
        {user && Object.keys(user).length === 0 && <Svg src={logo} />}
      </MobileLogoWrapper>
      {!!user && user.role === 'admin' ? (
        <AdminControls>
          <NotificationIconWrapper onClick={openNotificationDropdown}>
            <Svg src={topbarnotification} />
            {unreadCount > 0 && (
              <NotificationBadge>{unreadCount}</NotificationBadge>
            )}
          </NotificationIconWrapper>
          <AvatarIcon src={'/favicon1.png'} onClick={openUserDropdown} />
        </AdminControls>
      ) : (
        <ButtonContainer>
          {isMobile ? (
            <>
              <MobileButton onClick={() => {}}>
                <Svg src={supporticon} />
                <MobileButtonText>Help</MobileButtonText>
              </MobileButton>
              <MobileButton onClick={() => {}}>
                <Svg src={globeicon} />
                <MobileButtonText>Website</MobileButtonText>
              </MobileButton>
            </>
          ) : (
            <>
              <Button title="Help & Support" />
              <Button title="Visit Website" />
            </>
          )}
        </ButtonContainer>
      )}
    </StyleTopBar>
  )
}

export default Topbar

const DropdownMenu = styled.ul`
  width: 230px;
  padding: 8px 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 14px;
  color: ${theme.colors.text};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f5f5f5d6;
  }

  svg {
    color: #555;
  }
`

const NotificationIconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px;
`

const NotificationBadge = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  border: 2px solid white;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const MobileLogoWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 0;
  transform: translateX(-40px);
  @media (max-width: 768px) {
    padding: 8px;
    transform: translateX(0px);
  }
`

const AdminControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

const AvatarIcon = styled.img`
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`

const MobileButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${theme.colors.text};

  &:active {
    opacity: 0.7;
  }
`

const MobileButtonText = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`
